'use client';

import React from 'react';
import * as PhosphorIcons from '@phosphor-icons/react';
import { Box, Card as MuiCard, CardContent, CardMedia, Stack, Typography } from '@unity/cloud-ui';
import z from 'zod';
import { getComponentDocumentationUrl } from '@/utils/get-component-doc-url';
import { Link } from '../link';

export const Card = (props: z.input<typeof cardSchema>) => {
  const { title, children, href = '', icon, iconSrc, src, alt } = props;

  const getPhosphorIcon = (iconName) => {
    const availableIcons = Object.keys(PhosphorIcons);
    if (!iconName || !availableIcons.includes(iconName)) {
      return null;
    }

    return PhosphorIcons[iconName];
  };

  const Icon = getPhosphorIcon(icon);

  return (
    <Link href={href}>
      <MuiCard
        variant="outlined"
        sx={({ vars, transitions }) => ({
          height: '100%',
          borderRadius: '16px',
          transition: `background-color ${transitions.duration.standard}ms`,
          backgroundColor: vars.palette.background.paperElevation0,
          '&:hover': {
            backgroundColor: vars.palette.background.paperElevation1,
          },
        })}
      >
        <CardContent sx={{ padding: 2, ':last-child': { paddingBottom: 2 } }}>
          <Stack gap={1}>
            {src && <CardMedia sx={{ mb: 1 }} component="img" height="250" image={src} alt={alt || title} />}
            {!!Icon && (
              <Box color="primary.main">
                <Icon title={icon} size={24} aria-hidden="true" focusable="false" />
              </Box>
            )}
            {iconSrc && (
              <div>
                <Box component="img" src={iconSrc} alt={alt || title} height={32} />
              </div>
            )}
            <Box>
              <Stack gap={0.5}>
                <Typography variant="h3" component="span">
                  {title}
                </Typography>
                <Typography component="span" color="text.secondary">
                  {children}
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </MuiCard>
    </Link>
  );
};

const documentationUrl = getComponentDocumentationUrl('cards');

const descriptions = {
  card: {
    title: 'The title of the card. If alt property is not set, the title will be used as the alt text.',
    src: 'The source of the image of the card.\n\nTo set the alternate (alt) of this iconSrc, use the `alt` property.',
    icon: 'The name of the icon to use in the card.',
    iconSrc:
      'The source (src) of the icon to use in the card.\n\n To set the alternate (alt) of this iconSrc, use the `alt` property.',
    alt: 'The alternate (alt) text of the `src` | `iconSrc` property. If alt property is not set, the title will be used as the alt text.',
    href: 'The `url` to use when there is a click on the card.',
    children: 'The content of the card. Can be anything.',
    image: 'Deprecated. Use `iconSrc` or `src` instead.',
    description: 'Deprecated. Use `children` instead.',
    buttonLink: 'Deprecated. Use `href` instead.',
    buttonLabel: 'Deprecated. Use `title` instead.',
    schema: `Display a card with an icon, title, and description. \n\n\n\n**Example**\n\`\`\`mdx\n<Card\n  title="Title"\n  iconSrc="[href]"\n  alt="[altText]"\n  href="[href]"\n>\n  The content of the card\n</Card>\n\`\`\`\n\n${documentationUrl}`,
  },
};

export const cardSchema = z
  .object({
    title: z.string().describe(descriptions.card.title),
    src: z.string().describe(descriptions.card.src).optional(),
    icon: z.string().describe(descriptions.card.icon).optional(),
    iconSrc: z.string().describe(descriptions.card.iconSrc).optional(),
    alt: z.string().describe(descriptions.card.alt).optional(),
    href: z.string().describe(descriptions.card.href).optional(),
    children: z.any().describe(descriptions.card.children).optional(),
    image: z.string().describe(descriptions.card.image).optional(),
    description: z.string().describe(descriptions.card.description).optional(),
    buttonLink: z
      .object({
        url: z.string().optional(),
        page: z.string().optional(),
        openInNewTab: z.boolean().optional(),
      })
      .describe(descriptions.card.buttonLink)
      .optional(),
    buttonLabel: z.string().describe(descriptions.card.buttonLabel).optional(),
  })
  .describe(descriptions.card.schema);

export default Card;
